import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Tabs, Tab } from "react-bootstrap";
import Select from "../../common-components/select/Select.jsx";
import "./CaseScenarios.scss";
import { conversionOptions } from "./dropDownOptions.js";
import CustomButton from "../../common-components/button/Button.jsx";
import SyntaxHighlighter from "react-syntax-highlighter";
import { github } from "react-syntax-highlighter/dist/esm/styles/hljs";
import ReactMarkdown from "react-markdown";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useDispatch, useSelector } from "react-redux";
import { caseScenarioActions } from "../../state/reducers/caseScenarios.js";
import Banner from "../../common-components/banner/Banner.jsx";
import bannerMessages from "../../common/bannerMessage.js";

const customStyle = {
  backgroundColor: "#fff"
};

function CaseScenarios() {
  const dispatch = useDispatch();
  const { conversionData = {}, loading } = useSelector((state) => state.caseScenarios);
  const [conversion, setConversion] = useState("");
  const [showTarget, setShowTarget] = useState(false);
  const [showScenarioDetails, setShowScenarioDetails] = useState(false);
  const [activeSourceTab, setActiveSourceTab] = useState("source_code");
  const [activeTargetTab, setActiveTargetTab] = useState("target_code");
  const sourceTabsRef = useRef(null);
  const targetTabsRef = useRef(null);
  const [bannerMessage, setBannerMessage] = useState();

  useEffect(() => {
    if (conversion) {
      const selectedOption = conversionOptions.find(option => option.value === conversion);
      if (selectedOption && selectedOption.action) {
        dispatch(caseScenarioActions[selectedOption.action]());
      }
    }
  }, [conversion, dispatch]);

  useEffect(() => {
    if (conversionData) {
      const sourceTabs = Object.keys(conversionData).filter(key => key.startsWith("source_"));
      const targetTabs = Object.keys(conversionData).filter(key => 
        key.startsWith("target_") || key === "tdd" || key === "judge_response"
      );
      
      if (sourceTabs.length > 0) {
        setActiveSourceTab(sourceTabs[0]);
      }
      
      if (targetTabs.length > 0) {
        setActiveTargetTab(targetTabs[0]);
      }
    }
  }, [conversionData]);

  const handleConversionChange = (event) => {
    const value = event.target.value;
    setConversion(value);
    setShowTarget(false);
    setShowScenarioDetails(true);
    setBannerMessage(bannerMessages.find(banner => banner.value === event.target.value))
    //setActiveSourceTab("source_code");
    
    // Force re-render of source tabs to show source_code tab
    if (sourceTabsRef.current) {
      sourceTabsRef.current.forceUpdate();
    }
  };

  const handleStartConversion = () => {
    setShowTarget(true);
    //setActiveTargetTab("target_code");
    
    // Force re-render of target tabs to show target_code tab
    if (targetTabsRef.current) {
      targetTabsRef.current.forceUpdate();
    }
  };

  const resetFields = () => {
    setConversion("");
    setShowTarget(false);
    setShowScenarioDetails(false);
    setActiveSourceTab("");
    setActiveTargetTab("");
    setBannerMessage("")
  };

  const renderContent = (content, isMarkdown) => {
    return isMarkdown ? (
      <ReactMarkdown>{content}</ReactMarkdown>
    ) : (
      <SyntaxHighlighter style={github} customStyle={customStyle} wrapLongLines={true}>
        {content}
      </SyntaxHighlighter>
    );
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const getTabTitle = (key, conversionType) => {
    if (key === "tdd") return "TDD";
    if (key === "judge_response") return "Judge Response";

    const baseName = key.replace("source_", "").replace("target_", "").replace("_", " ");
    const capitalizedName = capitalizeFirstLetter(baseName);
    
    if (key.startsWith("source_")) {
      return `${capitalizeFirstLetter(conversionType.split("-")[0])} ${capitalizedName}`;
    } else if (key.startsWith("target_")) {
      return `${capitalizeFirstLetter(conversionType.split("-")[1])} ${capitalizedName}`;
    }
    return `${capitalizedName}`;
  };

  const renderTabs = (type) => {
    if (!conversion || !conversionData) return null;

    const selectedOption = conversionOptions.find(option => option.value === conversion);
    if (!selectedOption) return null;

    const tabs = Object.keys(conversionData).filter(key => 
      key.startsWith(type) || (type === "target_" && (key === "tdd" || key === "judge_response"))
    );

    const activeKey = type === "source_" ? activeSourceTab : activeTargetTab;
    const setActiveKey = type === "source_" ? setActiveSourceTab : setActiveTargetTab;

    return (
      <Col xs={6}>
        <Tabs 
          activeKey={activeKey} 
          onSelect={(k) => setActiveKey(k)}
          id={`${type}-wrapper`}
          ref={type === "source_" ? sourceTabsRef : targetTabsRef}
          className="cs-source-code-wrapper"
        >
          {tabs.map((tab, index) => {
            const isMarkdown = tab === "tdd" || tab === "judge_response";
            return (
              <Tab key={tab} eventKey={tab} title={getTabTitle(tab, selectedOption.value, index + 1)} className="cs-tab-content-wrapper">
                <div className="tab-content-wrapper">
                  <Scrollbars autoHeight autoHeightMax={500}>
                    {renderContent(conversionData[tab], isMarkdown)}
                  </Scrollbars>
                </div>
              </Tab>
            );
          })}
        </Tabs>
      </Col>
    );
  };

  return (
    <div className="page-wrapper code-conversion-page">
      <Container fluid>
        <Row>
          <Col>
            <div className="content-wrap">
              <h2 className="page-title">Case Scenarios</h2>
              <div className="code-conversion-wrapper-case">
                <Row className="mb-3">
                  <Col>
                    {bannerMessage && <Banner message={bannerMessage.message} icon={true} variant={bannerMessage.variant}/>}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Select
                      value={conversion}
                      options={conversionOptions}
                      placeholder={"Select Code Conversion"}
                      label="Code Conversion"
                      id="code-conversion"
                      layoutStyle="horizontal"
                      labelColSize={4}
                      inputColSize={8}
                      required
                      onChange={handleConversionChange}
                    />
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col className="text-end">
                    <CustomButton className="convert me-3" label="Start Conversion" onClick={handleStartConversion} disabled={loading} />
                    <CustomButton className="convert btn-secondary" label="Reset" onClick={resetFields} />
                  </Col>
                </Row>
              </div>
              {showScenarioDetails && (
                <div className="scenarios-details">
                  <Row>
                    <Col>
                      {conversionData && conversionData.description && (
                        <div className="validation-message">{conversionData.description}</div>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    {renderTabs("source_")}
                    {showTarget && renderTabs("target_")}
                  </Row>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default CaseScenarios;